export interface IColors {
  primary: string;
  transparent: string;
  text: string[];
  border: string[];
  shadow: string[];
  backdrop: string[];
  background: string[];
  error: string[];
  success: string[];
  tag: {
    blue: string;
    error: string;
    success: string;
    primary: string;
    yellow: string;
    red: string;
    green: string;
    grey: string;
  };
  pnl: {
    red: string;
    idle: string;
    green: string;
    neutral: string;
  };
  homeBanner: {
    gray: string;
    blue: string;
  };
  miniChart: {
    red: string[];
    green: string[];
  };
  marketNews: {
    background: string;
    text: string;
  };
  tooltip: {
    background: string;
    text: string;
  };
  progressBar: {
    background: string;
  };
  candlestick: {
    positive: string;
    negative: string;
  };
  popupAnnouncement: {
    textBold: string;
  };
  orderStatusTag: {
    violet: string;
    amber: string;
    cyan: string;
  };
  assetsDoughnut: TAssetDoughtnutColors;
}

export type TAssetDoughtnutColors = [string, string, string, string, string, string];

export const DEFAULT_CANDLE_STICK_COLORS_RGB: IColors['candlestick'] = {
  positive: 'rgb(0, 159, 66)',
  negative: 'rgb(246, 71, 93)'
};
export const DEFAULT_CANDLE_STICK_COLORS_HEX: IColors['candlestick'] = {
  positive: '#009f42',
  negative: '#f6475d'
};

const DEFAULT_DOUGHNUT_COLORS: TAssetDoughtnutColors = [
  'rgba(110, 88, 255, 0.8)',
  'rgba(242, 68, 85, 0.8)',
  'rgba(70, 201, 184, 0.8)',
  'rgba(54, 162, 235, 0.8)',
  'rgba(242, 142, 42, 0.8)',
  'rgba(199, 103, 220, 0.8)'
];

export const DARK_THEME_COLORS: IColors = {
  primary: 'rgb(0, 180, 201)',
  transparent: 'rgba(0, 0, 0, 0)',
  text: ['rgb(234, 236, 239)', 'rgb(172, 182, 196)', 'rgb(109, 119, 129)', 'rgb(69, 79, 89)', 'rgb(255, 255, 255)'],
  border: ['rgb(46, 53, 82)', 'rgb(66, 73, 102)', 'rgb(86, 93, 113)', 'rgb(106, 113, 123)'],
  shadow: ['0 0 4px rgba(0, 15, 30, 0.15)', '0 0 1px 4px rgba(10, 15, 20, 0.2)'],
  error: ['rgb(246, 71, 93)', 'rgb(255, 237, 239)'],
  success: ['rgb(76, 175, 79)', 'rgb(0, 159, 66)', 'rgb(236, 252, 240)'],
  backdrop: ['rgba(0, 10, 20, 0.8)'],
  background: [
    'rgb(29, 32, 47)',
    'rgb(40, 43, 58)',
    'rgb(62, 65, 79)',
    'rgb(82, 85, 99)',
    'rgb(102, 105, 119)',
    'rgb(40, 43, 58)',
    'rgb(29, 32, 47)'
  ],
  tag: {
    blue: 'rgb(0, 128, 255)',
    error: 'rgb(225, 74, 87)',
    success: 'rgb(76, 175, 79)',
    primary: 'rgb(84, 160, 182)',
    yellow: 'rgb(247, 170, 2)',
    red: 'rgb(246, 71, 93)',
    green: 'rgb(0, 159, 66)',
    grey: 'rgb(148, 148, 148)'
  },
  pnl: {
    red: 'rgb(246, 70, 93)',
    idle: 'rgb(79, 86, 97)',
    green: 'rgb(0, 159, 66)',
    neutral: 'rgb(33, 136, 255)'
  },
  homeBanner: {
    gray: 'rgb(67, 81, 105)',
    blue: 'rgb(60, 68, 162)'
  },
  miniChart: {
    red: ['rgba(246, 70, 93, 1)', 'rgba(246, 70, 93, 0.3)', 'rgba(246, 70, 93, 0.05)'],
    green: ['rgba(0, 159, 66, 1)', 'rgba(0, 159, 66, 0.3)', 'rgba(0, 159, 66, 0.05)']
  },
  marketNews: {
    background: 'rgba(0, 0, 0, 0.9)',
    text: 'rgb(255, 255, 255)'
  },
  tooltip: {
    background: 'rgb(230, 241, 244)',
    text: 'rgb(84, 160, 182)'
  },
  progressBar: {
    background: 'rgb(232, 236, 240)'
  },
  candlestick: DEFAULT_CANDLE_STICK_COLORS_RGB,
  popupAnnouncement: {
    textBold: 'rgb(234, 236, 239)'
  },
  orderStatusTag: {
    violet: 'rgb(134, 87, 255)',
    amber: 'rgb(236, 154, 0)',
    cyan: 'rgb(0, 188, 216)'
  },
  assetsDoughnut: DEFAULT_DOUGHNUT_COLORS
};

export const LIGHT_THEME_COLORS: IColors = {
  primary: 'rgb(0, 180, 201)',
  transparent: 'rgba(0, 0, 0, 0)',
  text: ['rgb(0, 9, 9)', 'rgb(129, 139, 139)', 'rgb(169, 179, 179)', 'rgb(209, 214, 224)', 'rgb(102, 112, 133)'],
  border: ['rgb(230, 234, 238)', 'rgb(210, 214, 218)', 'rgb(30, 34, 38)', 'rgb(50, 54, 58)'],
  error: ['rgb(246, 71, 93)', 'rgb(255, 237, 239)'],
  success: ['rgb(76, 175, 79)', 'rgb(0, 159, 66)', 'rgb(236, 252, 240)'],
  shadow: ['0 0 4px rgba(0, 15, 30, 0.15)'],
  backdrop: ['rgba(0, 10, 20, 0.5)'],
  background: [
    'rgb(255, 255, 255)',
    'rgb(243, 246, 249)',
    'rgb(231, 236, 239)',
    'rgb(211, 216, 219)',
    'rgb(191, 196, 199)',
    'rgb(250, 250, 250)',
    'rgb(243, 246, 249)'
  ],
  tag: {
    blue: 'rgb(0, 128, 255)',
    error: 'rgb(225, 74, 87)',
    success: 'rgb(76, 175, 79)',
    primary: 'rgb(84, 160, 182)',
    yellow: 'rgb(247, 170, 2)',
    red: 'rgb(246, 71, 93)',
    green: 'rgb(0, 159, 66)',
    grey: 'rgb(148, 148, 148)'
  },
  pnl: {
    red: 'rgb(246, 70, 93)',
    idle: 'rgb(180, 192, 204)',
    green: 'rgb(0, 159, 66)',
    neutral: 'rgb(33, 136, 255)'
  },
  homeBanner: {
    gray: 'rgb(67, 81, 105)',
    blue: 'rgb(60, 68, 162)'
  },
  miniChart: {
    red: ['rgba(246, 70, 93, 1)', 'rgba(246, 70, 93, 0.3)', 'rgba(246, 70, 93, 0.05)'],
    green: ['rgba(0, 159, 66, 1)', 'rgba(0, 159, 66, 0.3)', 'rgba(0, 159, 66, 0.05)']
  },
  marketNews: {
    background: 'rgba(0, 0, 0, 0.9)',
    text: 'rgb(255, 255, 255)'
  },
  tooltip: {
    background: 'rgb(230, 241, 244)',
    text: 'rgb(84, 160, 182)'
  },
  progressBar: {
    background: 'rgb(212, 217, 217)'
  },
  candlestick: DEFAULT_CANDLE_STICK_COLORS_RGB,
  popupAnnouncement: {
    textBold: 'rgb(69, 80, 87)' // SLATE 90
  },
  orderStatusTag: {
    violet: 'rgb(134, 87, 255)',
    amber: 'rgb(236, 154, 0)',
    cyan: 'rgb(0, 188, 216)'
  },
  assetsDoughnut: DEFAULT_DOUGHNUT_COLORS
};
